import { Clock, ExternalLink, Info, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemId } from "@/components/ui/system-id";
import type { Activity } from "@/hooks/api/use-account-activities";
import { useAccountPersons } from "@/hooks/api/use-account-persons";
import {
  type PageView,
  useWebSession,
  type WebSession as WebSessionType,
} from "@/hooks/api/use-web-session";
import { useTouchpointIdSidebarEnabled } from "@/hooks/use-feature-flags";
import { formatDate, formatTimeWithTimezone } from "@/lib/date-formatting";

interface ReferrerLinkProps {
  url: string;
}

function ReferrerLink({ url }: ReferrerLinkProps) {
  try {
    const hostname = new URL(url).hostname;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline flex items-center"
      >
        {hostname}
        <ExternalLink className="h-3 w-3 ml-1" />
      </a>
    );
  } catch {
    // If URL is invalid, display it as plain text without making it clickable
    return (
      <span className="text-muted-foreground flex items-center">
        {url} (invalid URL)
      </span>
    );
  }
}

interface WebSessionProps {
  activity: Activity;
  accountId?: string;
}

interface SessionSummaryProps {
  channel: string;
  session?: {
    page_view_count: number;
    start_timestamp: string;
    end_timestamp: string;
  };
}

function SessionSummary({ channel, session }: SessionSummaryProps) {
  if (!session)
    return (
      <p className="text-sm text-muted-foreground mt-2">
        <Badge
          variant="secondary"
          className="bg-accent hover:bg-accent text-accent-foreground"
        >
          {channel}
        </Badge>
      </p>
    );

  return (
    <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1 flex-wrap">
      {session.page_view_count}{" "}
      {session.page_view_count === 1 ? "page view" : "page views"} on{" "}
      {formatDate(session.start_timestamp)}
    </p>
  );
}

interface SessionDetailsProps {
  session: WebSessionType;
  personName: string;
  firstPageView?: PageView;
}

function SessionDetails({
  session,
  personName,
  firstPageView,
}: SessionDetailsProps) {
  if (!session) return null;

  const hasUTMParams =
    firstPageView?.utm_source ||
    firstPageView?.utm_medium ||
    firstPageView?.utm_campaign ||
    firstPageView?.utm_content ||
    firstPageView?.utm_term;
  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap gap-x-8 gap-y-2">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Started:</span>
          <span title={new Date(session.start_timestamp).toLocaleString()}>
            {formatTimeWithTimezone(session.start_timestamp)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Duration:</span>
          <span>
            {Math.round(
              (new Date(session.end_timestamp).getTime() -
                new Date(session.start_timestamp).getTime()) /
                60000,
            )}{" "}
            minutes
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Channel:</span>
          <span>{session.web_session_channel}</span>
        </div>
        {session.initial_referrer_url && (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Referrer:</span>
            <ReferrerLink url={session.initial_referrer_url} />
          </div>
        )}

        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">Visitor:</span>
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {personName}
          </span>
        </div>
      </div>

      {hasUTMParams && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-4 p-3 bg-muted/30 rounded-md">
          {firstPageView.utm_source && (
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">UTM Source</span>
              <span className="truncate" title={firstPageView.utm_source}>
                {firstPageView.utm_source}
              </span>
            </div>
          )}

          {firstPageView.utm_medium && (
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">UTM Medium</span>
              <span className="truncate" title={firstPageView.utm_medium}>
                {firstPageView.utm_medium}
              </span>
            </div>
          )}

          {firstPageView.utm_campaign && (
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">
                UTM Campaign
              </span>
              <span className="truncate" title={firstPageView.utm_campaign}>
                {firstPageView.utm_campaign}
              </span>
            </div>
          )}

          {firstPageView.utm_content && (
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">UTM Content</span>
              <span className="truncate" title={firstPageView.utm_content}>
                {firstPageView.utm_content}
              </span>
            </div>
          )}

          {firstPageView.utm_term && (
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">UTM Term</span>
              <span className="truncate" title={firstPageView.utm_term}>
                {firstPageView.utm_term}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface PageViewItemProps {
  pageView: PageView;
  showSeparator?: boolean;
}

function PageViewItem({ pageView, showSeparator = true }: PageViewItemProps) {
  return (
    <>
      <div className="relative">
        <div className="rounded-md px-2 py-1">
          <div className="flex justify-between items-center">
            <div className="text-sm">
              Viewed {pageView.page_type || "page"} webpage at {pageView.url}
            </div>
            <p
              className="text-xs text-muted-foreground ml-4 shrink-0 flex items-center gap-1"
              title={new Date(pageView.timestamp).toLocaleString()}
            >
              <Clock className="h-3 w-3" />
              {formatTimeWithTimezone(pageView.timestamp)}
            </p>
          </div>
        </div>
      </div>
      {showSeparator && <Separator className="my-4" />}
    </>
  );
}

function WebSessionSkeleton() {
  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-6">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-6 w-40" />
        </div>
      </div>

      <Separator className="my-6" />

      <Skeleton className="h-6 w-full mb-4" />

      <div className="space-y-6">
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="relative">
              <div className="flex justify-between items-center mb-2">
                <Skeleton className="h-5 w-[60%]" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Separator className="my-4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function WebSession({ activity, accountId }: WebSessionProps) {
  const { data: webSessionData, isLoading } = useWebSession(
    activity.session_id ?? null,
  );
  const { getPersonName } = useAccountPersons(accountId);
  const showTouchpointId = useTouchpointIdSidebarEnabled();
  const session = webSessionData?.session;
  const pageViews = webSessionData?.page_views || [];

  // Show loading state
  if (isLoading) {
    return <WebSessionSkeleton />;
  }

  // Get the person name for each page view or default to "Unknown User"
  const personName =
    pageViews.length > 0 && pageViews[0].person_id
      ? getPersonName(pageViews[0].person_id)
      : "Unknown User";

  // Get the first page view to extract common UTM parameters
  const firstPageView = pageViews.length > 0 ? pageViews[0] : undefined;

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="text-xl font-bold">Web Session by {personName}</h2>
          <SessionSummary channel={activity.channel} session={session} />
        </div>
      </div>

      <Separator className="my-6" />

      {session && (
        <SessionDetails
          session={session}
          personName={personName}
          firstPageView={firstPageView}
        />
      )}

      <Separator className="my-6" />

      <div className="space-y-2">
        <div className="space-y-2 overflow-y-auto pr-4 flex-1">
          {pageViews.length > 0 ? (
            pageViews.map((pageView, index) => (
              <PageViewItem
                key={pageView.id}
                pageView={pageView}
                showSeparator={index !== pageViews.length - 1}
              />
            ))
          ) : (
            <div className="text-sm text-muted-foreground text-center py-4">
              No page view data available
            </div>
          )}
        </div>
      </div>

      {session && showTouchpointId && (
        <div className="flex justify-end mt-6">
          <SystemId id={session.id} />
        </div>
      )}

      {!session && (
        <div className="flex items-center justify-center p-8 text-muted-foreground">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            <span>Session data not available</span>
          </div>
        </div>
      )}
    </div>
  );
}
